import { useEffect, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import { FormAutoComplete } from "@/components/form/FormAutoComplete";
import { useOrganizations } from "@/services/fhir/organization/useOrganizations";
import { useInsurancePlans } from "@/services/fhir/insuranceplan/useInsurancePlans";
import { FormTextInput } from "@/components/form/FormTextInput";

import { PostCoverage } from "@/services/fhir/coverage/PostCoverage";

// Interface para o formulário
export interface CoverageFormData {
  organization: { value: string; label: string } | null;
  insurancePlan: { value: string; label: string; display?: string } | null;
  cardNumber: string;
}

interface CoverageModalProps {
  open: boolean;
  isEditing: boolean;
  patientId: string;
  initialData?: Partial<CoverageFormData> & { id?: string };
  onClose: () => void;
  onSuccess: () => void;
}

export function CoverageModal({
  open,
  isEditing,
  patientId,
  initialData,
  onClose,
  onSuccess,
}: CoverageModalProps) {
  // Estado para controlar loading
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Estado para busca de organizações
  const [organizationSearchTerm, setOrganizationSearchTerm] = useState("");

  // Hook para buscar organizações
  const {
    organizations,
    loading: organizationsLoading,
    fetchOrganizations,
  } = useOrganizations();

  // Hook para buscar planos de saúde
  const {
    insurancePlans,
    loading: insurancePlansLoading,
    fetchInsurancePlans,
    clearInsurancePlans,
  } = useInsurancePlans();

  // Configuração do React Hook Form
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<CoverageFormData>({
    defaultValues: initialData || {
      organization: null,
      insurancePlan: null,
      cardNumber: "",
    },
  });

  // Observa o valor do campo organização para controlar habilitação dos planos
  const selectedOrganization = watch("organization");

  // Buscar organizações quando o modal abrir
  useEffect(() => {
    if (open) {
      fetchOrganizations({
        active: true,
        count: 50,
        type: "pay",
        content: organizationSearchTerm || initialData?.organization?.label,
      });
    }
  }, [open, organizationSearchTerm, fetchOrganizations]);

  // Buscar planos de saúde ao selecionar organização
  useEffect(() => {
    if (selectedOrganization?.value) {
      fetchInsurancePlans({
        ownedBy: selectedOrganization.value,
        count: 150,
      });
    } else {
      clearInsurancePlans();
      reset({
        ...watch(),
        insurancePlan: null,
      });
    }
  }, [
    selectedOrganization?.value,
    fetchInsurancePlans,
    clearInsurancePlans,
    reset,
    watch,
  ]);

  // Converter organizações para o formato do FormAutoComplete
  const organizationOptions = useMemo(() => {
    return organizations.map((org) => ({
      value: org.id || "",
      label: org.name || org.id || "",
    }));
  }, [organizations]);

  // Converter planos para o formato do autocomplete
  const insurancePlanOptions = useMemo(() => {
    return insurancePlans.map((plan) => ({
      value: plan.id || "",
      label: plan.name || "",
      display: plan.name || "",
    }));
  }, [insurancePlans]);

  // Handler para submeter o formulário
  const onSubmit = async (data: CoverageFormData) => {
    setIsSubmitting(true);
    try {
      const references = {
        patientId,
      };

      await PostCoverage(data, references);

      toast.success("Plano de saúde cadastrado com sucesso!");
      onSuccess();
      onClose();
    } catch (error) {
      toast.error("Erro ao cadastrar plano de saúde");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEditing ? "Editar Carteirinha" : "Nova Carteirinha"}
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={3} mt={2}>
          <Stack spacing={3}>
            <FormAutoComplete
              name="organization"
              control={control}
              label="Operadora"
              required={true}
              selectOptions={organizationOptions}
              loading={organizationsLoading}
              noOptionsText="Nenhuma operadora encontrada"
              onInputChange={(_, value) => {
                setOrganizationSearchTerm(value);
              }}
              disabled={isEditing}
            />

            <FormAutoComplete
              name="insurancePlan"
              control={control}
              label="Plano de Saúde"
              required={true}
              selectOptions={insurancePlanOptions}
              loading={insurancePlansLoading}
              disabled={!selectedOrganization?.value || isEditing}
              noOptionsText={
                !selectedOrganization?.value
                  ? "Selecione uma operadora primeiro"
                  : "Nenhum plano encontrado"
              }
            />

            <FormTextInput
              control={control}
              label="Número da Carteirinha"
              name="cardNumber"
              required={true}
              placeholder="Digite o número da sua carteirinha"
            />
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
        >
          {isSubmitting ? "Salvando..." : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
